import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { DollarSign, Landmark, MessageCircleMore, Save, Wallet } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell } from "recharts";

const formatCurrency = (value) => Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const METHOD_COLORS = ["#0f766e", "#0ea5e9", "#f59e0b", "#8b5cf6", "#ef4444", "#64748b"];

const ProgrammerFinance = () => {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [supportConfig, setSupportConfig] = useState({
    phone: "5586999613119",
    message: "ESTOU COM DÚVIDAS SOBRE O DIAGNÓSTICO E PRECISO DE AJUDA",
  });
  const [savingSupport, setSavingSupport] = useState(false);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/programmer/finance/overview`, {
        withCredentials: true,
        params: {
          ...(dateFrom ? { date_from: dateFrom } : {}),
          ...(dateTo ? { date_to: dateTo } : {}),
        },
      });
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar financeiro");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const fetchSupportConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/programmer/support-config`, { withCredentials: true });
      setSupportConfig({
        phone: response.data?.phone || "5586999613119",
        message: response.data?.message || "ESTOU COM DÚVIDAS SOBRE O DIAGNÓSTICO E PRECISO DE AJUDA",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao carregar configuração de suporte");
    }
  }, []);

  useEffect(() => {
    fetchSupportConfig();
  }, [fetchSupportConfig]);

  const toggleDiagnosticSettlement = async (paymentId, field, currentValue) => {
    const key = `${paymentId}-${field}`;
    setSavingKey(key);
    try {
      await axios.put(`${API}/programmer/diagnostic-payments/${paymentId}/settlement`, { [field]: !currentValue }, { withCredentials: true });
      toast.success("Repasse do diagnóstico atualizado");
      fetchOverview();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar repasse");
    } finally {
      setSavingKey("");
    }
  };

  const toggleConsultancySettlement = async (referralId, field, currentValue) => {
    const key = `${referralId}-${field}`;
    setSavingKey(key);
    try {
      await axios.put(`${API}/programmer/referrals/${referralId}/settlement`, { [field]: !currentValue }, { withCredentials: true });
      toast.success("Repasse da consultoria atualizado");
      fetchOverview();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar repasse");
    } finally {
      setSavingKey("");
    }
  };

  const saveSupportConfig = async () => {
    setSavingSupport(true);
    try {
      await axios.put(`${API}/programmer/support-config`, supportConfig, { withCredentials: true });
      toast.success("Configuração de suporte atualizada");
      fetchSupportConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar configuração de suporte");
    } finally {
      setSavingSupport(false);
    }
  };

  const paymentMethodChart = useMemo(
    () => (data?.payment_methods || []).map((item, index) => ({ ...item, color: METHOD_COLORS[index % METHOD_COLORS.length] })),
    [data]
  );

  return (
    <AdminLayout>
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="font-serif text-3xl text-navy">Financeiro do Programador</h1>
            <p className="text-slate-600">Acompanhe vendas de diagnóstico, contratos de consultoria e repasses internos.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 w-full md:w-auto">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            <Button onClick={fetchOverview} className="bg-navy hover:bg-navy/90">Aplicar período</Button>
          </div>
        </div>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <MessageCircleMore className="h-5 w-5 text-emerald-600" />
              Suporte da página inicial
            </CardTitle>
            <CardDescription>
              Defina o número e a mensagem do botão flutuante de ajuda exibido no canto inferior esquerdo da página inicial.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 lg:grid-cols-[280px,1fr,auto] gap-4 items-end">
            <div>
              <label className="text-sm text-slate-600 block mb-2">Número do WhatsApp</label>
              <Input
                value={supportConfig.phone}
                onChange={(e) => setSupportConfig((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="5586999613119"
              />
            </div>
            <div>
              <label className="text-sm text-slate-600 block mb-2">Mensagem inicial</label>
              <textarea
                value={supportConfig.message}
                onChange={(e) => setSupportConfig((prev) => ({ ...prev, message: e.target.value }))}
                className="flex min-h-[96px] w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                placeholder="Mensagem que será enviada no WhatsApp"
              />
            </div>
            <Button onClick={saveSupportConfig} disabled={savingSupport} className="bg-navy hover:bg-navy/90">
              <Save className="h-4 w-4 mr-2" />
              {savingSupport ? "Salvando..." : "Salvar suporte"}
            </Button>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
          </div>
        ) : !data ? null : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm text-slate-500">Vendas de diagnóstico</span><Wallet className="h-4 w-4 text-emerald-600" /></div>
                  <p className="text-2xl font-bold text-navy">{formatCurrency(data.summary.diagnostics_total)}</p>
                  <p className="text-xs text-slate-500 mt-2">{data.summary.diagnostics_count} venda(s) no período</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm text-slate-500">Repasse Juarez</span><DollarSign className="h-4 w-4 text-sky-600" /></div>
                  <p className="text-2xl font-bold text-navy">{formatCurrency(data.summary.diagnostics_juarez_amount + data.summary.company_juarez_amount)}</p>
                  <p className="text-xs text-slate-500 mt-2">Diagnósticos + participação da empresa</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm text-slate-500">Repasse Paulo</span><DollarSign className="h-4 w-4 text-violet-600" /></div>
                  <p className="text-2xl font-bold text-navy">{formatCurrency(data.summary.diagnostics_paulo_amount + data.summary.company_paulo_amount)}</p>
                  <p className="text-xs text-slate-500 mt-2">Diagnósticos + participação da empresa</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm text-slate-500">Consultorias fechadas</span><Landmark className="h-4 w-4 text-amber-600" /></div>
                  <p className="text-2xl font-bold text-navy">{formatCurrency(data.summary.consultancies_total)}</p>
                  <p className="text-xs text-slate-500 mt-2">{data.summary.consultancies_count} contrato(s) fechados</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Evolução financeira</CardTitle>
                  <CardDescription>Receita de diagnósticos e contratos fechados por mês.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={(data.diagnostics_by_month || []).map((item) => {
                      const consultancy = (data.consultancies_by_month || []).find((month) => month.month === item.month);
                      return {
                        month: item.month,
                        diagnosticos: item.total_sales,
                        consultorias: consultancy?.total_contracts || 0,
                      };
                    })}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="diagnosticos" fill="#0f766e" name="Diagnósticos" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="consultorias" fill="#f59e0b" name="Consultorias" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Formas de pagamento</CardTitle>
                  <CardDescription>Como os diagnósticos foram pagos no período.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={paymentMethodChart} dataKey="total" nameKey="label" innerRadius={60} outerRadius={95} paddingAngle={3}>
                        {paymentMethodChart.map((entry) => <Cell key={entry.method} fill={entry.color} />)}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Vendas de diagnóstico</CardTitle>
                <CardDescription>Repasse de 50% para Juarez e 50% para Paulo.</CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm min-w-[920px]">
                  <thead>
                    <tr className="border-b text-left text-slate-500">
                      <th className="py-3 pr-3">Empresa</th>
                      <th className="py-3 pr-3">Pagamento</th>
                      <th className="py-3 pr-3">Método</th>
                      <th className="py-3 pr-3">Valor</th>
                      <th className="py-3 pr-3">Juarez</th>
                      <th className="py-3 pr-3">Paulo</th>
                      <th className="py-3 pr-3">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.diagnostic_payments || []).map((payment) => (
                      <tr key={payment.payment_id} className="border-b align-top">
                        <td className="py-3 pr-3 font-medium text-navy">{payment.company_name}</td>
                        <td className="py-3 pr-3">{formatDate(payment.paid_at || payment.updated_at || payment.created_at)}</td>
                        <td className="py-3 pr-3">{payment.payment_method_label || "-"}</td>
                        <td className="py-3 pr-3">{formatCurrency(payment.amount)}</td>
                        <td className="py-3 pr-3">
                          <div>{formatCurrency(payment.juarez_amount)}</div>
                          <Badge className={payment.juarez_paid ? "bg-emerald-100 text-emerald-800 mt-1" : "bg-slate-100 text-slate-700 mt-1"}>{payment.juarez_paid ? "Pago" : "Pendente"}</Badge>
                        </td>
                        <td className="py-3 pr-3">
                          <div>{formatCurrency(payment.paulo_amount)}</div>
                          <Badge className={payment.paulo_paid ? "bg-emerald-100 text-emerald-800 mt-1" : "bg-slate-100 text-slate-700 mt-1"}>{payment.paulo_paid ? "Pago" : "Pendente"}</Badge>
                        </td>
                        <td className="py-3 pr-3 space-y-2">
                          <Button size="sm" variant="outline" disabled={savingKey === `${payment.payment_id}-juarez_paid`} onClick={() => toggleDiagnosticSettlement(payment.payment_id, "juarez_paid", payment.juarez_paid)}>
                            {payment.juarez_paid ? "Desfazer Juarez" : "Marcar Juarez"}
                          </Button>
                          <Button size="sm" variant="outline" disabled={savingKey === `${payment.payment_id}-paulo_paid`} onClick={() => toggleDiagnosticSettlement(payment.payment_id, "paulo_paid", payment.paulo_paid)}>
                            {payment.paulo_paid ? "Desfazer Paulo" : "Marcar Paulo"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Consultorias fechadas</CardTitle>
                <CardDescription>Valor do contrato, repasse do consultor, entrada da empresa e divisão dos sócios.</CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm min-w-[1180px]">
                  <thead>
                    <tr className="border-b text-left text-slate-500">
                      <th className="py-3 pr-3">Empresa</th>
                      <th className="py-3 pr-3">Consultor</th>
                      <th className="py-3 pr-3">Área</th>
                      <th className="py-3 pr-3">Contrato</th>
                      <th className="py-3 pr-3">Consultor</th>
                      <th className="py-3 pr-3">Empresa</th>
                      <th className="py-3 pr-3">Juarez</th>
                      <th className="py-3 pr-3">Paulo</th>
                      <th className="py-3 pr-3">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.consultancy_contracts || []).map((referral) => (
                      <tr key={referral.referral_id} className="border-b align-top">
                        <td className="py-3 pr-3 font-medium text-navy">{referral.company_name}</td>
                        <td className="py-3 pr-3">{referral.consultant_name}</td>
                        <td className="py-3 pr-3">{referral.area}</td>
                        <td className="py-3 pr-3">
                          <div>{formatCurrency(referral.contract_value)}</div>
                          <div className="text-xs text-slate-500">{formatDate(referral.contract_recorded_at)}</div>
                        </td>
                        <td className="py-3 pr-3">
                          <div>{formatCurrency(referral.consultant_amount)}</div>
                          <Badge className={referral.consultant_paid ? "bg-emerald-100 text-emerald-800 mt-1" : "bg-slate-100 text-slate-700 mt-1"}>{referral.consultant_paid ? "Pago" : "Pendente"}</Badge>
                        </td>
                        <td className="py-3 pr-3">
                          <div>{formatCurrency(referral.company_amount)}</div>
                          <Badge className={referral.company_received ? "bg-emerald-100 text-emerald-800 mt-1" : "bg-slate-100 text-slate-700 mt-1"}>{referral.company_received ? "Recebido" : "Pendente"}</Badge>
                        </td>
                        <td className="py-3 pr-3">
                          <div>{formatCurrency(referral.juarez_amount)}</div>
                          <Badge className={referral.juarez_paid ? "bg-emerald-100 text-emerald-800 mt-1" : "bg-slate-100 text-slate-700 mt-1"}>{referral.juarez_paid ? "Pago" : "Pendente"}</Badge>
                        </td>
                        <td className="py-3 pr-3">
                          <div>{formatCurrency(referral.paulo_amount)}</div>
                          <Badge className={referral.paulo_paid ? "bg-emerald-100 text-emerald-800 mt-1" : "bg-slate-100 text-slate-700 mt-1"}>{referral.paulo_paid ? "Pago" : "Pendente"}</Badge>
                        </td>
                        <td className="py-3 pr-3 space-y-2">
                          <Button size="sm" variant="outline" disabled={savingKey === `${referral.referral_id}-consultant_paid`} onClick={() => toggleConsultancySettlement(referral.referral_id, "consultant_paid", referral.consultant_paid)}>
                            {referral.consultant_paid ? "Desfazer consultor" : "Marcar consultor"}
                          </Button>
                          <Button size="sm" variant="outline" disabled={savingKey === `${referral.referral_id}-company_received`} onClick={() => toggleConsultancySettlement(referral.referral_id, "company_received", referral.company_received)}>
                            {referral.company_received ? "Desfazer empresa" : "Marcar empresa"}
                          </Button>
                          <Button size="sm" variant="outline" disabled={savingKey === `${referral.referral_id}-juarez_paid`} onClick={() => toggleConsultancySettlement(referral.referral_id, "juarez_paid", referral.juarez_paid)}>
                            {referral.juarez_paid ? "Desfazer Juarez" : "Marcar Juarez"}
                          </Button>
                          <Button size="sm" variant="outline" disabled={savingKey === `${referral.referral_id}-paulo_paid`} onClick={() => toggleConsultancySettlement(referral.referral_id, "paulo_paid", referral.paulo_paid)}>
                            {referral.paulo_paid ? "Desfazer Paulo" : "Marcar Paulo"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default ProgrammerFinance;
